import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Mail, RefreshCw, Plus, Trash2, Edit3, Send, Reply, ReplyAll, Forward, Paperclip,
  Star, StarOff, Inbox, FileText, Archive, Mailbox, X, Search,
  CheckCircle2, AlertCircle, Settings, CheckSquare, Square, Eye,
  Trash, Image as ImageIcon, ChevronLeft, ChevronRight, Download, Receipt, PanelLeft, MailWarning,
} from "lucide-react";
import { ImportSummaryModal } from "./Financiar";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const FOLDERS = [
  { key: "inbox", label: "Inbox", icon: Inbox },
  { key: "sent", label: "Trimise", icon: Send },
  { key: "drafts", label: "Ciorne", icon: FileText },
  { key: "archive", label: "Arhivă", icon: Archive },
  { key: "trash", label: "Coș de gunoi", icon: Trash },
];

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const today = new Date();
    if (d.toDateString() === today.toDateString()) return d.toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" });
    return d.toLocaleDateString("ro-RO", { day: "2-digit", month: "short" });
  } catch { return iso; }
}

// Attachment fetch via axios so the JWT Authorization header stays attached.
// A raw `<a href>` opens the URL in a new tab / native webview WITHOUT the
// bearer token — this is why iOS Safari + PWA users got "Neautentificat".
async function fetchAttachmentBlob(mid, idx, mode = "inline") {
  const url = `${API}/mail/messages/${encodeURIComponent(mid)}/attachment/${idx}${mode === "download" ? "?mode=download" : ""}`;
  const r = await axios.get(url, { responseType: "blob" });
  return r.data;
}

async function openAttachment(mid, idx, filename) {
  try {
    const blob = await fetchAttachmentBlob(mid, idx, "inline");
    const objUrl = URL.createObjectURL(blob);
    // Try opening in a new tab; fall back to same-tab (iOS Safari PWA
    // blocks window.open with a null return sometimes).
    const w = window.open(objUrl, "_blank");
    if (!w) window.location.assign(objUrl);
    // Revoke after 60s so long-running previews still work.
    setTimeout(() => URL.revokeObjectURL(objUrl), 60000);
  } catch (e) {
    toast.error("Eroare deschidere atașament: " + (e.response?.status === 401 ? "sesiune expirată, re-loghează-te" : e.message));
  }
}

async function downloadAttachment(mid, idx, filename) {
  try {
    const blob = await fetchAttachmentBlob(mid, idx, "download");
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objUrl;
    a.download = filename || `attachment-${idx}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objUrl), 10000);
  } catch (e) {
    toast.error("Eroare descărcare: " + (e.response?.status === 401 ? "sesiune expirată" : e.message));
  }
}

async function downloadAllAttachments(mid, attachments) {
  if (!attachments || attachments.length === 0) return;
  toast.info(`Se descarcă ${attachments.length} atașament(e)...`);
  for (let i = 0; i < attachments.length; i++) {
    // Sequential download so Safari doesn't drop simultaneous fetches on
    // large PDF bundles (iOS caps parallel XHRs to ~6 aggressively on PWAs).
    await downloadAttachment(mid, i, attachments[i].filename);
  }
  toast.success("Descărcare completă");
}

export default function MailPage() {
  const [accounts, setAccounts] = useState([]);
  const [activeAccount, setActiveAccount] = useState("all");
  const [folder, setFolder] = useState("inbox");
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedFull, setSelectedFull] = useState(null);
  const [selectedThread, setSelectedThread] = useState(null);  // array of full messages
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState("");
  const [unreadCounts, setUnreadCounts] = useState({});
  const [accountModal, setAccountModal] = useState(null); // null = closed, {} = new, {id,...} = edit
  const [showCompose, setShowCompose] = useState(null); // {to, subject, body, in_reply_to, references, account_id}
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sidebarOpen, setSidebarOpen] = useState(false); // phone: accounts/folders drawer
  const closeViewer = () => { setSelectedFull(null); setSelectedId(null); setSelectedThread(null); };
  const markOpenUnread = async () => {
    const ids = selectedThread ? selectedThread.map(m => m.id) : selectedFull ? [selectedFull.id] : [];
    if (!ids.length) return;
    try {
      await axios.post(`${API}/mail/messages/bulk-mark`, { ids, is_read: false });
      setMessages(prev => prev.map(m => ids.includes(m.id) ? { ...m, is_read: false } : m));
      closeViewer();
      toast.success("Marcat ca necitit");
      loadCounts();
    } catch (e) {
      toast.error("Nu s-a putut marca: " + (e.response?.data?.detail || e.message));
    }
  };
  const [bulkBusy, setBulkBusy] = useState(false);
  const [page, setPage] = useState(1);
  const PER_PAGE = 50;

  // Reset to page 1 whenever folder/account/search/filter changes
  useEffect(() => { setPage(1); }, [folder, activeAccount, search, unreadOnly]);

  // ---- Loaders ----
  const loadAccounts = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/mail/accounts`);
      setAccounts(r.data?.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  }, []);

  const loadCounts = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/mail/unread-count`);
      setUnreadCounts(r.data?.per_account || {});
    } catch (_e) { /* ignore */ }
  }, []);

  const loadMessages = useCallback(async () => {
    setLoading(true);
    try {
      const params = { folder, page, per_page: PER_PAGE };
      if (activeAccount && activeAccount !== "all") params.account_id = activeAccount;
      if (search.trim()) params.q = search.trim();
      if (unreadOnly) params.unread_only = true;
      const r = await axios.get(`${API}/mail/messages`, { params });
      setMessages(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, [activeAccount, folder, search, unreadOnly, page]);

  useEffect(() => { loadAccounts(); loadCounts(); /* eslint-disable-line */ }, [loadAccounts, loadCounts]);
  useEffect(() => { loadMessages(); /* eslint-disable-line */ }, [loadMessages]);

  // Auto-refresh the message list every 20s so new mails appear without a
  // manual click. Only refresh when the tab is visible — avoids hammering
  // the API while the user is in another tab. Also calls /unread-count.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      if (typeof document !== "undefined" && document.hidden) return;
      try {
        await Promise.all([loadMessages(), loadCounts()]);
      } catch (_e) { /* ignore */ }
    };
    const iv = setInterval(tick, 20000);
    // Refresh immediately when the tab becomes visible again
    const onVis = () => { if (!document.hidden) tick(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { cancelled = true; clearInterval(iv); document.removeEventListener("visibilitychange", onVis); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadMessages, loadCounts]);

  // Periodic refresh of unread counts (every 60s) — kept as a safety net in
  // case the message refresh above is paused (e.g., tab in background).
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const r = await axios.get(`${API}/mail/unread-count`);
        if (!cancelled) setUnreadCounts(r.data?.per_account || {});
      } catch (_e) { /* ignore */ }
    };
    const iv = setInterval(tick, 60000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  // ---- Actions ----
  const handleSync = async () => {
    setSyncing(true);
    let newTotal = 0;
    try {
      const targets = activeAccount === "all" ? accounts : accounts.filter(a => a.id === activeAccount);
      for (const a of targets) {
        const r = await axios.post(`${API}/mail/accounts/${a.id}/sync`);
        newTotal += r.data?.new || 0;
      }
      toast.success(`Sincronizat. ${newTotal} mesaje noi.`);
      await loadMessages(); await loadCounts();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setSyncing(false); }
  };

  const handleDeepResync = async () => {
    const daysStr = window.prompt(
      "Deep re-sync — recuperează mail-uri din ultimele X zile (max 90).\n" +
      "Util pentru recuperarea mail-urilor șterse accidental (le aduce înapoi din Gmail).",
      "30"
    );
    if (daysStr === null) return;
    const days = Math.max(1, Math.min(parseInt(daysStr, 10) || 30, 90));
    setSyncing(true);
    let totalNew = 0, totalRestored = 0;
    try {
      const targets = activeAccount === "all" ? accounts : accounts.filter(a => a.id === activeAccount);
      for (const a of targets) {
        const r = await axios.post(`${API}/mail/accounts/${a.id}/deep-resync?days=${days}`);
        totalNew += r.data?.new || 0;
        totalRestored += r.data?.restored_from_trash || 0;
      }
      toast.success(`Deep re-sync (${days} zile): ${totalNew} noi · ${totalRestored} restaurate din trash.`);
      await loadMessages(); await loadCounts();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setSyncing(false); }
  };

  const openMessage = async (m) => {
    setSelectedId(m.id);
    setSelectedThread(null);
    if (!m.is_read) {
      setMessages((prev) => prev.map(x => x.id === m.id ? { ...x, is_read: true } : x));
    }
    try {
      const r = await axios.get(`${API}/mail/messages/${encodeURIComponent(m.id)}`);
      setSelectedFull(r.data);
      if (!m.is_read) {
        axios.post(`${API}/mail/messages/${encodeURIComponent(m.id)}/mark`, { is_read: true })
          .then(() => loadCounts())
          .catch(() => {});
      }
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const openThread = async (thread) => {
    // Open a grouped Gmail thread — load every message body and show them stacked
    setSelectedId(thread.head.id);
    setSelectedFull(null);
    setSelectedThread(thread.msgs.map(m => ({ ...m, _loading: true })));
    // Optimistic: mark all unread messages in thread as read locally
    const unreadIds = thread.msgs.filter(m => !m.is_read).map(m => m.id);
    if (unreadIds.length > 0) {
      setMessages(prev => prev.map(m => unreadIds.includes(m.id) ? { ...m, is_read: true } : m));
      axios.post(`${API}/mail/messages/bulk-mark`, { ids: unreadIds, is_read: true })
        .then(() => loadCounts())
        .catch(() => {});
    }
    try {
      // Fetch full bodies in parallel
      const fulls = await Promise.all(
        thread.msgs.map(m => axios.get(`${API}/mail/messages/${encodeURIComponent(m.id)}`).then(r => r.data).catch(() => m))
      );
      // Sort oldest → newest for readable thread view
      fulls.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
      setSelectedThread(fulls);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const toggleStar = async (m) => {
    try {
      await axios.post(`${API}/mail/messages/${encodeURIComponent(m.id)}/mark`, { is_starred: !m.is_starred });
      setMessages((prev) => prev.map(x => x.id === m.id ? { ...x, is_starred: !m.is_starred } : x));
      if (selectedFull?.id === m.id) setSelectedFull({ ...selectedFull, is_starred: !m.is_starred });
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const handleReply = () => {
    if (!selectedFull) return;
    const acc = accounts.find(a => a.id === selectedFull.account_id);
    const myEmail = (acc?.email || "").toLowerCase();
    // Prefer Reply-To header (RFC 5322) over From when it's present and isn't
    // our own mailbox. Fixes the case where the IMAP server returns the
    // recipient address in some "From" variant on certain providers (e.g.
    // forwarded threads or aliased mailboxes).
    const replyDest = (selectedFull.reply_to_addr || "").trim();
    const fromAddr  = (selectedFull.from_addr || "").trim();
    let pickedTo = replyDest && replyDest.toLowerCase() !== myEmail ? replyDest : fromAddr;
    // Final safety: if the chosen destination is still our own mailbox, fall
    // back to the first non-self address from the original To list — this
    // happens when we reply to a SENT-folder message that lives in INBOX too.
    if (pickedTo && pickedTo.toLowerCase() === myEmail) {
      const origTo = Array.isArray(selectedFull.to_list) ? selectedFull.to_list : (selectedFull.to_addrs || []);
      const alt = origTo.find(e => e && e.toLowerCase() !== myEmail);
      if (alt) pickedTo = alt;
    }
    setShowCompose({
      account_id: selectedFull.account_id,
      to: pickedTo ? [pickedTo] : [],
      cc: [],
      subject: selectedFull.subject?.startsWith("Re:") ? selectedFull.subject : `Re: ${selectedFull.subject || ""}`,
      body: `\n\n\n---\nLa ${selectedFull.date?.slice(0,16)}, ${selectedFull.from_name || selectedFull.from_addr} a scris:\n> ${(selectedFull.body_text || "").split("\n").join("\n> ")}`,
      in_reply_to: selectedFull.message_id || "",
      references: selectedFull.message_id || "",
      signature: acc?.signature_html || "",
    });
  };

  const handleReplyAll = () => {
    if (!selectedFull) return;
    const acc = accounts.find(a => a.id === selectedFull.account_id);
    const myEmail = (acc?.email || "").toLowerCase();
    const replyDest = (selectedFull.reply_to_addr || "").trim();
    const fromAddr  = (selectedFull.from_addr || "").trim();
    // Primary destination = Reply-To if set (and not us), else original From
    const primary = replyDest && replyDest.toLowerCase() !== myEmail ? replyDest : fromAddr;
    // Build To = primary destination + everyone in original To (minus our own address)
    // Field names: backend stores `to_list` / `cc_list` (not `to_addrs`).
    const origTo = Array.isArray(selectedFull.to_list) ? selectedFull.to_list : (selectedFull.to_addrs || []);
    const origCc = Array.isArray(selectedFull.cc_list) ? selectedFull.cc_list : (selectedFull.cc_addrs || []);
    const dedup = (arr) => Array.from(new Set(arr.filter(Boolean).map(e => e.trim())));
    const toList = dedup([primary, ...origTo].filter(e => e && e.toLowerCase() !== myEmail));
    const ccList = dedup(origCc.filter(e => e && e.toLowerCase() !== myEmail && !toList.map(x=>x.toLowerCase()).includes(e.toLowerCase())));
    setShowCompose({
      account_id: selectedFull.account_id,
      to: toList,
      cc: ccList,
      subject: selectedFull.subject?.startsWith("Re:") ? selectedFull.subject : `Re: ${selectedFull.subject || ""}`,
      body: `\n\n\n---\nLa ${selectedFull.date?.slice(0,16)}, ${selectedFull.from_name || selectedFull.from_addr} a scris:\n> ${(selectedFull.body_text || "").split("\n").join("\n> ")}`,
      in_reply_to: selectedFull.message_id || "",
      references: selectedFull.message_id || "",
      signature: acc?.signature_html || "",
    });
  };

  const handleForward = () => {
    if (!selectedFull) return;
    const acc = accounts.find(a => a.id === selectedFull.account_id);
    const hasAtts = (selectedFull.attachments || []).length > 0;
    let include_atts = false;
    if (hasAtts) {
      include_atts = window.confirm(
        `Mesajul are ${selectedFull.attachments.length} atașament(e).\n\n` +
        `OK = forwardeaz CU atașamente\n` +
        `Cancel = forwardeaz FĂRĂ atașamente`
      );
    }
    setShowCompose({
      account_id: selectedFull.account_id,
      to: [],
      cc: [],
      subject: selectedFull.subject?.toLowerCase().startsWith("fwd:") ? selectedFull.subject : `Fwd: ${selectedFull.subject || ""}`,
      body:
        `\n\n\n---------- Mesaj redirecționat ----------\n` +
        `De la: ${selectedFull.from_name || ""} <${selectedFull.from_addr || ""}>\n` +
        `Data: ${selectedFull.date?.slice(0,16) || ""}\n` +
        `Subiect: ${selectedFull.subject || ""}\n` +
        `Către: ${(selectedFull.to_addrs || []).join(", ")}\n\n` +
        (selectedFull.body_text || ""),
      signature: acc?.signature_html || "",
      forward_from_mid: selectedFull.id,
      include_forward_attachments: include_atts,
    });
  };

  const handleNewMail = () => {
    const acc = activeAccount !== "all" ? accounts.find(a=>a.id===activeAccount) : accounts[0];
    if (!acc) { toast.error("Adaugă întâi un cont email."); return; }
    setShowCompose({
      account_id: acc.id, to: [], cc: [], subject: "", body: "",
      signature: acc.signature_html || "",
    });
  };

  // ---- Render ----
  const selectedAccount = useMemo(() => accounts.find(a => a.id === activeAccount), [accounts, activeAccount]);

  // Gmail thread compaction. Returns a flat list of "rows":
  //   [{ kind:"single", msg }] or [{ kind:"group", msgs:[...], head:<latest>, count:N }]
  // Rules:
  //   • A row group is created only when 2+ messages share the same
  //     thread_key AND the originating account is a Gmail account.
  //   • Bragus / other accounts always render flat.
  const rows = useMemo(() => {
    if (folder !== "inbox") {
      return messages.map(m => ({ kind: "single", id: m.id, msg: m }));
    }
    const gmailAccountIds = new Set(
      accounts.filter(a => (a.email || "").toLowerCase().endsWith("@gmail.com") || (a.email || "").toLowerCase().endsWith("@googlemail.com")).map(a => a.id)
    );
    // Bucket by thread_key only for Gmail accounts
    const groups = new Map();
    const flat = [];
    for (const m of messages) {
      if (!gmailAccountIds.has(m.account_id)) {
        flat.push({ kind: "single", id: m.id, msg: m });
        continue;
      }
      const key = m.thread_key || `${m.account_id}:${m.from_addr || ""}:${m.subject || ""}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(m);
    }
    // Convert groups: single → "single", multi → "group" capped at 50 per row
    const MAX_THREAD = 50;
    // Sort each group's messages newest → oldest by REAL timestamp so the row
    // head reflects the most recent activity, regardless of date string format.
    const ts = (d) => {
      const t = Date.parse(d || "");
      return Number.isFinite(t) ? t : 0;
    };
    for (const [key, msgs] of groups) {
      msgs.sort((a, b) => ts(b.date) - ts(a.date));
      if (msgs.length === 1) {
        flat.push({ kind: "single", id: msgs[0].id, msg: msgs[0] });
      } else {
        // Split into chunks of MAX_THREAD so a hot sender doesn't form
        // one giant 500-message blob.
        for (let i = 0; i < msgs.length; i += MAX_THREAD) {
          const slice = msgs.slice(i, i + MAX_THREAD);
          flat.push({
            kind: "group", id: `g:${key}:${i}`,
            msgs: slice, head: slice[0], count: slice.length,
            unread: slice.filter(m => !m.is_read).length,
            has_attachments: slice.some(m => m.has_attachments),
            is_starred: slice.some(m => m.is_starred),
          });
        }
      }
    }
    // Sort the whole list newest-first using parsed timestamps (handles ISO,
    // RFC 2822 and any other format Date.parse understands).
    flat.sort((a, b) => {
      const da = a.kind === "single" ? a.msg.date : a.head.date;
      const db = b.kind === "single" ? b.msg.date : b.head.date;
      return ts(db) - ts(da);
    });
    return flat;
  }, [messages, accounts, folder]);

  // ---- Selection helpers ----
  const allSelectable = useMemo(() => {
    // Flatten all msg ids that participate in the current list
    const ids = [];
    for (const r of rows) {
      if (r.kind === "single") ids.push(r.msg.id);
      else r.msgs.forEach(m => ids.push(m.id));
    }
    return ids;
  }, [rows]);
  const isRowSelected = (r) => {
    if (r.kind === "single") return selectedIds.has(r.msg.id);
    return r.msgs.every(m => selectedIds.has(m.id));
  };
  const isRowPartial = (r) => r.kind === "group" && r.msgs.some(m => selectedIds.has(m.id)) && !r.msgs.every(m => selectedIds.has(m.id));
  const toggleRowSelection = (r) => {
    const ids = r.kind === "single" ? [r.msg.id] : r.msgs.map(m => m.id);
    setSelectedIds(prev => {
      const n = new Set(prev);
      const allOn = ids.every(id => n.has(id));
      if (allOn) ids.forEach(id => n.delete(id));
      else ids.forEach(id => n.add(id));
      return n;
    });
  };
  const toggleAll = () => {
    setSelectedIds(prev => {
      if (allSelectable.every(id => prev.has(id))) return new Set();
      return new Set(allSelectable);
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const bulkMarkRead = async (asRead) => {
    if (selectedIds.size === 0) return;
    setBulkBusy(true);
    const ids = Array.from(selectedIds);
    try {
      await axios.post(`${API}/mail/messages/bulk-mark`, { ids, is_read: asRead });
      setMessages(prev => prev.map(m => ids.includes(m.id) ? { ...m, is_read: asRead } : m));
      clearSelection();
      loadCounts();
      toast.success(`${ids.length} mesaje marcate ca ${asRead ? "citite" : "necitite"}`);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBulkBusy(false); }
  };

  const markAllReadInView = async () => {
    setBulkBusy(true);
    try {
      const params = {};
      if (activeAccount !== "all") params.account_id = activeAccount;
      params.folder = folder;
      const r = await axios.post(`${API}/mail/mark-all-read`, null, { params });
      toast.success(`${r.data?.count || 0} mesaje marcate ca citite`);
      setMessages(prev => prev.map(m => ({ ...m, is_read: true })));
      clearSelection();
      loadCounts();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBulkBusy(false); }
  };

  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const n = selectedIds.size;
    const isTrash = folder === "trash";
    const confirmMsg = isTrash
      ? `Ștergi DEFINITIV ${n} mesaje? Acțiunea nu poate fi anulată.`
      : `Sigur muți ${n} mesaje în Coș?`;
    if (!window.confirm(confirmMsg)) return;
    setBulkBusy(true);
    const ids = Array.from(selectedIds);
    try {
      const url = isTrash ? `${API}/mail/messages/bulk-delete-forever` : `${API}/mail/messages/bulk-delete`;
      await axios.post(url, { ids });
      setMessages(prev => prev.filter(m => !ids.includes(m.id)));
      if (selectedFull && ids.includes(selectedFull.id)) { setSelectedFull(null); setSelectedId(null); }
      clearSelection();
      loadCounts();
      toast.success(isTrash ? `${n} mesaje șterse definitiv` : `${n} mesaje mutate în Coș`);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBulkBusy(false); }
  };

  const bulkRestore = async () => {
    if (selectedIds.size === 0) return;
    setBulkBusy(true);
    const ids = Array.from(selectedIds);
    try {
      await axios.post(`${API}/mail/messages/bulk-restore`, { ids });
      setMessages(prev => prev.filter(m => !ids.includes(m.id)));
      clearSelection();
      loadCounts();
      toast.success(`${ids.length} mesaje restaurate`);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBulkBusy(false); }
  };

  const deleteSingle = async (m) => {
    if (!m) return;
    const isTrash = (m.folder || folder) === "trash";
    const confirmMsg = isTrash ? "Șterge DEFINITIV acest mesaj?" : "Mută acest mesaj în Coș?";
    if (!window.confirm(confirmMsg)) return;
    try {
      const url = isTrash
        ? `${API}/mail/messages/${encodeURIComponent(m.id)}/forever`
        : `${API}/mail/messages/${encodeURIComponent(m.id)}`;
      await axios.delete(url);
      setMessages(prev => prev.filter(x => x.id !== m.id));
      if (selectedFull?.id === m.id) { setSelectedFull(null); setSelectedId(null); }
      loadCounts();
      toast.success(isTrash ? "Mesaj șters definitiv" : "Mesaj mutat în Coș");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <div data-testid="mail-page" className="flex h-[calc(100vh-3.5rem)] flex-col bg-zinc-950 text-zinc-100 md:h-screen">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-2.5">
        <div className="flex items-center gap-2.5">
          <div className="rounded-lg bg-gradient-to-br from-indigo-500 to-violet-500 p-1.5 shadow-lg shadow-indigo-500/20">
            <Mailbox className="h-4 w-4 text-white" />
          </div>
          <div>
            <h1 className="text-sm font-semibold text-zinc-50">Mail</h1>
            <p className="text-[10px] text-zinc-500">Inbox unificat · multi-cont</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="mail-new-btn" onClick={handleNewMail} className="inline-flex items-center gap-1.5 rounded-md border border-indigo-500/50 bg-indigo-500/20 px-3 py-1.5 text-xs font-medium text-indigo-100 hover:bg-indigo-500/30">
            <Plus className="h-3.5 w-3.5" /> Mail nou
          </button>
          <button data-testid="mail-sync-btn" onClick={handleSync} disabled={syncing} className="inline-flex items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs font-medium text-zinc-200 hover:border-violet-500/40 hover:text-violet-200 disabled:opacity-50">
            <RefreshCw className={`h-3.5 w-3.5 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Sincronizare…" : "Sincronizează"}
          </button>
          <button data-testid="mail-deep-resync-btn" onClick={handleDeepResync} disabled={syncing} title="Recuperează mail-uri din ultimele 30-90 zile (util pentru mail-uri șterse accidental)" className="inline-flex items-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-xs font-medium text-amber-200 hover:bg-amber-500/20 disabled:opacity-50">
            <RefreshCw className={`h-3.5 w-3.5 ${syncing ? "animate-spin" : ""}`} />
            Deep re-sync
          </button>
        </div>
      </div>

      <div className="flex min-w-0 flex-1 overflow-hidden">
        {/* Sidebar & list are always visible on desktop. On mobile they
            hide when a message is open — see conditional classes below. */}
        {/* Sidebar accounts + folders */}
        <aside data-testid="mail-sidebar" className={`${sidebarOpen ? "fixed inset-y-0 left-0 z-50 flex w-64 shadow-2xl" : "hidden"} flex-col overflow-y-auto border-r border-zinc-800 bg-zinc-950 lg:static lg:z-auto lg:flex lg:w-56 lg:shadow-none`}>
          <button
            data-testid="mail-sidebar-close-btn"
            onClick={() => setSidebarOpen(false)}
            className="flex items-center justify-between border-b border-zinc-900 px-3 py-3 text-xs font-medium text-zinc-300 lg:hidden"
          >
            Conturi și foldere <X className="h-4 w-4" />
          </button>
          <div className="border-b border-zinc-900 p-3">
            <p className="mb-2 text-[9px] font-semibold uppercase tracking-wider text-zinc-500">Conturi</p>
            <button
              data-testid="acc-all"
              onClick={() => { setActiveAccount("all"); setSidebarOpen(false); }}
              className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs ${activeAccount === "all" ? "bg-violet-500/15 text-violet-200" : "text-zinc-400 hover:bg-zinc-900"}`}
            >
              <Inbox className="h-3.5 w-3.5" /> Toate conturile
              {Object.values(unreadCounts).reduce((s,v)=>s+v,0) > 0 && (
                <span className="ml-auto rounded-full bg-violet-500/20 px-1.5 text-[9px] font-semibold text-violet-100">{Object.values(unreadCounts).reduce((s,v)=>s+v,0)}</span>
              )}
            </button>
            {accounts.map((a) => {
              const u = unreadCounts[a.id] || 0;
              const isActive = activeAccount === a.id;
              return (
                <div key={a.id} className="group relative flex items-center">
                  <button
                    data-testid={`acc-${a.id}`}
                    onClick={() => { setActiveAccount(a.id); setSidebarOpen(false); }}
                    className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs ${isActive ? "bg-violet-500/15 text-violet-200" : "text-zinc-400 hover:bg-zinc-900"}`}
                  >
                    <Mail className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="truncate">{a.name}</span>
                    {u > 0 && (
                      <span className="ml-auto rounded-full bg-violet-500/20 px-1.5 text-[9px] font-semibold text-violet-100">{u}</span>
                    )}
                  </button>
                  <button
                    data-testid={`acc-edit-${a.id}`}
                    onClick={() => setAccountModal(a)}
                    className="absolute right-1 hidden rounded p-1 text-zinc-500 hover:text-zinc-200 group-hover:block"
                    title="Editează cont"
                  ><Settings className="h-3 w-3" /></button>
                </div>
              );
            })}
            <button
              data-testid="add-acc-btn"
              onClick={() => setAccountModal({})}
              className="mt-1 flex w-full items-center gap-2 rounded-md border border-dashed border-zinc-800 px-2 py-1.5 text-xs text-zinc-500 hover:border-indigo-500/40 hover:text-indigo-200"
            >
              <Plus className="h-3.5 w-3.5" /> Adaugă cont email
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-3">
            <p className="mb-2 text-[9px] font-semibold uppercase tracking-wider text-zinc-500">Foldere</p>
            {FOLDERS.map((f) => {
              const Icon = f.icon;
              const active = folder === f.key;
              return (
                <button
                  key={f.key}
                  data-testid={`folder-${f.key}`}
                  onClick={() => { setFolder(f.key); setSelectedId(null); setSelectedFull(null); setSidebarOpen(false); }}
                  className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs ${active ? "bg-indigo-500/15 text-indigo-200 font-medium" : "text-zinc-400 hover:bg-zinc-900"}`}
                >
                  <Icon className="h-3.5 w-3.5" /> {f.label}
                </button>
              );
            })}
            <button
              data-testid="filter-unread"
              onClick={() => setUnreadOnly(v => !v)}
              className={`mt-3 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs ${unreadOnly ? "bg-emerald-500/15 text-emerald-200" : "text-zinc-400 hover:bg-zinc-900"}`}
            >
              <span className={`h-2 w-2 rounded-full ${unreadOnly ? "bg-emerald-400" : "bg-zinc-700"}`} />
              Doar necitite
            </button>
          </div>
        </aside>
        {sidebarOpen && (
          <div data-testid="mail-sidebar-backdrop" onClick={() => setSidebarOpen(false)} className="fixed inset-0 z-40 bg-black/60 lg:hidden" />
        )}

        {/* Message list */}
        <section className={`${selectedId ? "hidden lg:flex" : "flex"} w-full lg:w-[420px] flex-shrink-0 flex-col border-r border-zinc-800`}>
          <div className="border-b border-zinc-800 p-2">
            <div className="flex items-center gap-2">
            <button
              data-testid="mail-sidebar-toggle-btn"
              onClick={() => setSidebarOpen(true)}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-300 lg:hidden"
              title="Conturi și foldere"
            >
              <PanelLeft className="h-3.5 w-3.5" />
              <span className="max-w-[90px] truncate">{FOLDERS.find(f => f.key === folder)?.label || "Foldere"}</span>
            </button>
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-500" />
              <input
                data-testid="mail-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Caută în mailuri…"
                className="w-full rounded-md border border-zinc-800 bg-zinc-900 pl-7 pr-2 py-1.5 text-xs text-zinc-200 placeholder:text-zinc-600 focus:border-indigo-500/50 focus:outline-none"
              />
            </div>
            </div>
            <div className="mt-1.5 flex items-center justify-between gap-2">
              <p className="text-[10px] text-zinc-500">
                {total} mesaje{folder !== "inbox" && ` în ${FOLDERS.find(f=>f.key===folder)?.label || folder}`}
                {total > PER_PAGE && <span className="ml-1 text-zinc-600">· {((page-1)*PER_PAGE)+1}–{Math.min(page*PER_PAGE, total)}</span>}
              </p>
              <div className="flex items-center gap-1">
                {total > PER_PAGE && (
                  <>
                    <button data-testid="mail-page-prev"
                      onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1 || loading}
                      title="Pagina anterioară"
                      className="rounded border border-zinc-800 bg-zinc-900 p-1 text-zinc-400 hover:border-zinc-700 disabled:opacity-30">
                      <ChevronLeft className="h-3 w-3" />
                    </button>
                    <span className="text-[10px] text-zinc-400 font-mono">{page}/{Math.max(1, Math.ceil(total / PER_PAGE))}</span>
                    <button data-testid="mail-page-next"
                      onClick={() => setPage(p => p + 1)} disabled={page >= Math.ceil(total / PER_PAGE) || loading}
                      title="Pagina următoare"
                      className="rounded border border-zinc-800 bg-zinc-900 p-1 text-zinc-400 hover:border-zinc-700 disabled:opacity-30">
                      <ChevronRight className="h-3 w-3" />
                    </button>
                  </>
                )}
                <button
                  data-testid="mark-all-read-btn"
                  onClick={markAllReadInView}
                  disabled={bulkBusy}
                  className="inline-flex items-center gap-1 rounded-md border border-zinc-800 bg-zinc-900 px-2 py-0.5 text-[10px] text-zinc-400 hover:border-emerald-500/40 hover:text-emerald-200 disabled:opacity-50"
                  title="Marchează toate necitite ca citite"
                >
                  <Eye className="h-2.5 w-2.5" /> Mark all as read
                </button>
              </div>
            </div>
          </div>
          {selectedIds.size > 0 && (
            <div data-testid="bulk-toolbar" className="flex flex-wrap items-center justify-between gap-2 border-b border-emerald-500/30 bg-emerald-500/10 px-3 py-2">
              <div className="flex items-center gap-2">
                <button onClick={toggleAll} className="rounded p-0.5 hover:bg-emerald-500/20">
                  {allSelectable.every(id => selectedIds.has(id)) ? <CheckSquare className="h-3.5 w-3.5 text-emerald-200" /> : <Square className="h-3.5 w-3.5 text-emerald-200" />}
                </button>
                <span className="text-[11px] font-medium text-emerald-200">{selectedIds.size} selectate</span>
              </div>
              <div className="flex flex-wrap gap-1">
                <button data-testid="bulk-read-btn" onClick={() => bulkMarkRead(true)} disabled={bulkBusy} className="rounded border border-emerald-500/40 bg-emerald-500/20 px-2 py-0.5 text-[10px] font-medium text-emerald-100 hover:bg-emerald-500/30 disabled:opacity-50">Citite</button>
                <button data-testid="bulk-unread-btn" onClick={() => bulkMarkRead(false)} disabled={bulkBusy} className="rounded border border-zinc-700 bg-zinc-800 px-2 py-0.5 text-[10px] text-zinc-300 hover:border-zinc-600 disabled:opacity-50">Necitite</button>
                {folder === "trash" && (
                  <button data-testid="bulk-restore-btn" onClick={bulkRestore} disabled={bulkBusy} className="inline-flex items-center gap-1 rounded border border-emerald-500/40 bg-emerald-500/15 px-2 py-0.5 text-[10px] font-medium text-emerald-100 hover:bg-emerald-500/25 disabled:opacity-50">
                    Restore
                  </button>
                )}
                <button data-testid="bulk-delete-btn" onClick={bulkDelete} disabled={bulkBusy} className="inline-flex items-center gap-1 rounded border border-rose-500/40 bg-rose-500/20 px-2 py-0.5 text-[10px] font-medium text-rose-200 hover:bg-rose-500/30 disabled:opacity-50">
                  <Trash className="h-2.5 w-2.5" /> {folder === "trash" ? "Delete forever" : "Șterge"}
                </button>
                <button onClick={clearSelection} className="rounded p-0.5 text-zinc-400 hover:text-zinc-100" title="Deselectează"><X className="h-3 w-3" /></button>
              </div>
            </div>
          )}
          <div data-testid="message-list" className="flex-1 overflow-y-auto">
            {loading && <p className="px-4 py-3 text-xs text-zinc-500">Se încarcă…</p>}
            {!loading && rows.length === 0 && (
              <div className="py-12 text-center">
                <Mailbox className="mx-auto h-8 w-8 text-zinc-700" />
                <p className="mt-2 text-xs text-zinc-500">Niciun mesaj.</p>
              </div>
            )}
            {rows.map((r) => {
              const m = r.kind === "single" ? r.msg : r.head;
              const accountName = accounts.find(a => a.id === m.account_id)?.name || "";
              const isSelected = selectedId === m.id;
              const hasUnread = r.kind === "single" ? !m.is_read : (r.unread > 0);
              const checked = isRowSelected(r);
              const partial = isRowPartial(r);
              const onRowClick = () => {
                if (r.kind === "single") openMessage(m);
                else openThread(r);
              };
              return (
                <div
                  key={r.id}
                  data-testid={r.kind === "group" ? `thread-${m.thread_key || m.id}` : `msg-${m.uid}`}
                  className={`group relative flex w-full items-start gap-2 border-b border-zinc-900 px-3 py-2.5 text-left transition ${isSelected ? "bg-indigo-500/10" : "hover:bg-zinc-900/60"} ${hasUnread ? "border-l-2 border-l-indigo-500" : ""}`}
                >
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleRowSelection(r); }}
                    className="mt-0.5 flex-shrink-0 rounded p-0.5 hover:bg-zinc-800"
                    data-testid={`select-${r.id}`}
                    title="Selectează"
                  >
                    {checked ? <CheckSquare className="h-3.5 w-3.5 text-emerald-300" /> : partial ? <CheckSquare className="h-3.5 w-3.5 text-emerald-300/40" /> : <Square className="h-3.5 w-3.5 text-zinc-600 hover:text-zinc-400" />}
                  </button>
                  <button onClick={onRowClick} className="min-w-0 flex-1 text-left">
                    <div className="mb-0.5 flex items-center justify-between gap-2">
                      <span className={`truncate text-xs ${hasUnread ? "font-semibold text-zinc-50" : "text-zinc-300"}`}>
                        {m.from_name || m.from_addr}
                        {r.kind === "group" && (
                          <span className="ml-1.5 inline-flex items-center rounded-full bg-zinc-800 px-1.5 py-0.5 text-[10px] font-semibold text-zinc-300">{r.count}</span>
                        )}
                      </span>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <span className="text-[10px] text-zinc-500">{fmtDate(m.date)}</span>
                      </div>
                    </div>
                    <p className={`mb-0.5 line-clamp-1 text-[11px] ${hasUnread ? "text-zinc-100" : "text-zinc-400"}`}>{m.subject || "(fără subiect)"}</p>
                    <div className="flex items-center gap-1.5 text-[9px] text-zinc-600">
                      <span className="rounded bg-zinc-900 px-1 py-0.5 uppercase tracking-wider">{accountName}</span>
                      {(r.kind === "single" ? m.has_attachments : r.has_attachments) && <Paperclip className="h-2.5 w-2.5" />}
                      {(r.kind === "single" ? m.is_starred : r.is_starred) && <Star className="h-2.5 w-2.5 fill-amber-400 text-amber-400" />}
                      {r.kind === "group" && r.unread > 0 && <span className="rounded-full bg-indigo-500/20 px-1 text-[8px] font-semibold text-indigo-200">{r.unread} noi</span>}
                    </div>
                  </button>
                  {/* Buton STERGE per rand (User request Feb 2026):
                      vizibil mereu pe mobile, aparent la hover pe desktop. */}
                  {r.kind === "single" && (
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteSingle(m); }}
                      data-testid={`row-delete-${r.id}`}
                      title="Șterge mesaj"
                      className="absolute right-2 bottom-2 rounded-md border border-rose-500/40 bg-rose-500/10 p-1 text-rose-300 opacity-100 transition hover:border-rose-500/70 hover:bg-rose-500/25 md:opacity-0 md:group-hover:opacity-100"
                    >
                      <Trash className="h-3 w-3" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Message viewer */}
        <main className={`${selectedId ? "flex" : "hidden lg:flex"} min-w-0 flex-1 flex-col overflow-hidden`}>
          {(selectedFull || selectedThread) && (
            <div className="flex items-center justify-between gap-2 border-b border-zinc-800 bg-zinc-900 px-3 py-2 lg:hidden">
              <button
                data-testid="mail-mobile-back-btn"
                onClick={closeViewer}
                className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm font-medium text-indigo-300"
              >
                <ChevronLeft className="h-4 w-4" /> Înapoi
              </button>
              <button
                data-testid="mail-mobile-unread-btn"
                onClick={markOpenUnread}
                className="inline-flex items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-800 px-3 py-1.5 text-xs font-medium text-zinc-200"
              >
                <MailWarning className="h-3.5 w-3.5" /> Necitit
              </button>
            </div>
          )}
          {!selectedFull && !selectedThread ? (
            <div className="flex flex-1 items-center justify-center text-center">
              <div>
                <Mail className="mx-auto h-12 w-12 text-zinc-800" />
                <p className="mt-3 text-sm text-zinc-500">Selectează un mesaj.</p>
              </div>
            </div>
          ) : selectedThread ? (
            <ThreadViewer
              msgs={selectedThread}
              accounts={accounts}
              folder={folder}
              onClose={() => { setSelectedThread(null); setSelectedId(null); }}
              onReply={() => {
                const last = selectedThread[selectedThread.length - 1];
                setSelectedFull(last);
                handleReply();
                setSelectedThread(null);
              }}
              onReplyAll={() => {
                const last = selectedThread[selectedThread.length - 1];
                setSelectedFull(last);
                handleReplyAll();
                setSelectedThread(null);
              }}
              onForward={() => {
                const last = selectedThread[selectedThread.length - 1];
                setSelectedFull(last);
                handleForward();
                setSelectedThread(null);
              }}
              onDeleteOne={async (m) => {
                if (folder === "trash") {
                  if (!window.confirm("Șterge definitiv acest mesaj?")) return;
                  await axios.delete(`${API}/mail/messages/${encodeURIComponent(m.id)}/forever`);
                } else {
                  await axios.delete(`${API}/mail/messages/${encodeURIComponent(m.id)}`);
                }
                setMessages(prev => prev.filter(x => x.id !== m.id));
                setSelectedThread(prev => prev.filter(x => x.id !== m.id));
              }}
            />
          ) : (
            <MessageViewer
              msg={selectedFull}
              account={accounts.find(a => a.id === selectedFull.account_id)}
              onBack={null}
              onReply={handleReply}
              onReplyAll={handleReplyAll}
              onForward={handleForward}
              onStar={() => toggleStar(selectedFull)}
              onDelete={() => deleteSingle(selectedFull)}
              folder={folder}
              onRestore={async () => {
                await axios.post(`${API}/mail/messages/${encodeURIComponent(selectedFull.id)}/restore`);
                setMessages(prev => prev.filter(x => x.id !== selectedFull.id));
                setSelectedFull(null); setSelectedId(null);
                toast.success("Mesaj restaurat");
                loadCounts();
              }}
              onDeleteForever={async () => {
                if (!window.confirm("Ștergi acest mesaj DEFINITIV? Acțiunea nu poate fi anulată.")) return;
                await axios.delete(`${API}/mail/messages/${encodeURIComponent(selectedFull.id)}/forever`);
                setMessages(prev => prev.filter(x => x.id !== selectedFull.id));
                setSelectedFull(null); setSelectedId(null);
                toast.success("Mesaj șters definitiv");
              }}
            />
          )}
        </main>
      </div>

      {accountModal !== null && (
        <AccountModal
          initial={accountModal}
          onClose={() => setAccountModal(null)}
          onSaved={async () => { await loadAccounts(); setAccountModal(null); }}
        />
      )}

      {showCompose && (
        <ComposeModal
          initial={showCompose}
          accounts={accounts}
          onClose={() => setShowCompose(null)}
          onSent={async () => { setShowCompose(null); await loadMessages(); }}
        />
      )}
    </div>
  );
}

function ThreadViewer({ msgs, accounts, folder, onClose, onReply, onReplyAll, onForward, onDeleteOne }) {
  const head = msgs[msgs.length - 1] || msgs[0];
  const accountName = accounts.find(a => a.id === head.account_id)?.name || "";
  return (
    <>
      <div className="border-b border-zinc-800 p-4">
        <div className="mb-1 flex flex-wrap items-start justify-between gap-3">
          <h2 className="min-w-0 flex-1 basis-full break-words text-base font-semibold leading-tight text-zinc-50 sm:basis-auto">{head.subject || "(fără subiect)"}</h2>
          <div className="flex flex-wrap gap-1">
            <button onClick={onClose} className="rounded-md p-1.5 text-zinc-500 hover:bg-zinc-900 hover:text-zinc-200"><X className="h-4 w-4" /></button>
            <button onClick={onReply} className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-indigo-500/50 bg-indigo-500/20 px-3 py-1.5 text-xs font-medium text-indigo-100 hover:bg-indigo-500/30">
              <Reply className="h-3.5 w-3.5" /> Răspunde
            </button>
            <button onClick={onReplyAll} className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-indigo-500/30 bg-indigo-500/10 px-3 py-1.5 text-xs font-medium text-indigo-200 hover:bg-indigo-500/20">
              <ReplyAll className="h-3.5 w-3.5" /> Răspunde tuturor
            </button>
            <button onClick={onForward} className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-zinc-700 bg-zinc-800 px-3 py-1.5 text-xs font-medium text-zinc-200 hover:border-zinc-600">
              <Forward className="h-3.5 w-3.5" /> Redirecționează
            </button>
          </div>
        </div>
        <p className="text-[10px] uppercase tracking-wider text-zinc-500">{accountName} · {msgs.length} mesaje în conversație</p>
      </div>
      <div data-testid="thread-viewer" className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden bg-zinc-950 p-4 space-y-3">
        {msgs.map((m, i) => (
          <details key={m.id || i} open={i === msgs.length - 1} className="group rounded-lg border border-zinc-800 bg-zinc-900/40">
            <summary className="flex cursor-pointer items-center justify-between gap-3 px-4 py-2.5 text-xs hover:bg-zinc-900/70">
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-zinc-100">{m.from_name || m.from_addr} <span className="text-zinc-600">&lt;{m.from_addr}&gt;</span></p>
                <p className="text-[10px] text-zinc-500">{m.date ? new Date(m.date).toLocaleString("ro-RO") : ""}{m.has_attachments && <> · <Paperclip className="inline h-2.5 w-2.5" /> atașamente</>}</p>
              </div>
              <button onClick={(e) => { e.preventDefault(); onDeleteOne(m); }} className="rounded p-1 text-zinc-500 hover:bg-rose-500/10 hover:text-rose-300" title={folder === "trash" ? "Șterge definitiv" : "Mută în Coș"}>
                <Trash className="h-3.5 w-3.5" />
              </button>
            </summary>
            <div className="border-t border-zinc-800 p-4">
              {(m.attachments || []).length > 0 && (
                <div className="mb-3 flex flex-wrap gap-1.5">
                  {m.attachments.map((a, j) => (
                    <span key={j} className="inline-flex items-center gap-0.5 rounded-md border border-zinc-800 bg-zinc-900 text-[10px] text-zinc-300 overflow-hidden">
                      <button onClick={() => openAttachment(m.id, j, a.filename)}
                        title="Deschide"
                        className="inline-flex items-center gap-1 px-2 py-1 hover:bg-zinc-800/60 hover:text-indigo-200">
                        <Paperclip className="h-3 w-3" /> {a.filename}
                      </button>
                      <button onClick={() => downloadAttachment(m.id, j, a.filename)}
                        title="Descarcă pe disk"
                        data-testid={`thread-att-${j}-download`}
                        className="border-l border-zinc-800 px-1.5 py-1 text-zinc-500 hover:bg-zinc-800/60 hover:text-emerald-300">
                        <Download className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              {m._loading ? (
                <p className="text-xs text-zinc-500">Se încarcă...</p>
              ) : m.body_html ? (
                <div className="mail-html overflow-x-auto break-words rounded bg-white p-4 text-sm text-gray-900" dangerouslySetInnerHTML={{ __html: m.body_html }} />
              ) : (
                <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-zinc-200">{m.body_text || "(fără conținut)"}</pre>
              )}
            </div>
          </details>
        ))}
      </div>
    </>
  );
}

function MessageViewer({ msg, account, onBack, onReply, onReplyAll, onForward, onStar, onDelete, folder, onRestore, onDeleteForever }) {
  const [invoiceBusy, setInvoiceBusy] = useState(false);
  const [invoiceSummary, setInvoiceSummary] = useState(null);
  // Save-to-Facturi-Lunare popup state
  const [lunarePickerOpen, setLunarePickerOpen] = useState(false);
  const [lunareBusy, setLunareBusy] = useState(false);
  const [lunareSummary, setLunareSummary] = useState(null);
  // Per-attachment state in the popup: {[idx]: {selected, month}}
  const [lunareRows, setLunareRows] = useState({});

  // "Adauga factura la Financiar" — descarca atasamentele PDF ale mesajului,
  // le trimite catre `POST /api/facturi/bulk-import-pdf?country=RO` (acelasi
  // endpoint ca butonul Import bulk PDF din Financiar), apoi deschide
  // acelasi ImportSummaryModal pentru editare + toggle platit/neplatit.
  const importInvoicesFromMail = async () => {
    const pdfs = (msg.attachments || []).filter(a => (a.filename || "").toLowerCase().endsWith(".pdf"));
    if (pdfs.length === 0) {
      toast.error("Mesajul nu are atasamente PDF.");
      return;
    }
    setInvoiceBusy(true);
    try {
      const fd = new FormData();
      for (let i = 0; i < msg.attachments.length; i++) {
        const a = msg.attachments[i];
        if (!(a.filename || "").toLowerCase().endsWith(".pdf")) continue;
        const blob = await fetchAttachmentBlob(msg.id, i, "inline");
        fd.append("files", new File([blob], a.filename, { type: "application/pdf" }));
      }
      const r = await axios.post(`${API}/financiar/facturi/bulk-import-pdf`, fd, {
        params: { country: "RO" },
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      setInvoiceSummary(r.data);
      if (r.data?.imported > 0) {
        toast.success(`${r.data.imported}/${r.data.total_files} facturi importate.`);
      } else {
        toast.error("0 facturi importate — verifica erorile in popup.");
      }
    } catch (e) {
      toast.error("Import esuat: " + (e?.response?.data?.detail || e.message));
    } finally {
      setInvoiceBusy(false);
    }
  };

  const hasPdfAttachments = (msg.attachments || []).some(a => (a.filename || "").toLowerCase().endsWith(".pdf"));

  // Open the „Adauga la Facturi Lunare" picker — initialize per-attachment
  // state (all PDFs selected by default, month left blank so backend auto-
  // detects it from the invoice date via Gemini; user can override).
  const openLunarePicker = () => {
    const rows = {};
    (msg.attachments || []).forEach((a, idx) => {
      if ((a.filename || "").toLowerCase().endsWith(".pdf")) {
        rows[idx] = { selected: true, month: "" };  // "" = auto-detect
      }
    });
    setLunareRows(rows);
    setLunarePickerOpen(true);
  };

  const submitLunareSave = async () => {
    const picked = Object.entries(lunareRows).filter(([, v]) => v.selected);
    if (picked.length === 0) { toast.error("Selectează cel puțin un PDF."); return; }
    setLunareBusy(true);
    try {
      const fd = new FormData();
      const overrides = {};
      for (const [idxStr, row] of picked) {
        const idx = Number(idxStr);
        const a = msg.attachments[idx];
        if (!a) continue;
        const blob = await fetchAttachmentBlob(msg.id, idx, "inline");
        fd.append("files", new File([blob], a.filename, { type: "application/pdf" }));
        if (row.month && /^\d{4}-\d{2}$/.test(row.month)) {
          overrides[a.filename] = row.month;
        }
      }
      const params = { country: "RO" };
      if (Object.keys(overrides).length) params.month_overrides = JSON.stringify(overrides);
      const r = await axios.post(`${API}/financiar/monthly-invoices/save-from-mail`, fd, {
        params, headers: { "Content-Type": "multipart/form-data" }, timeout: 180000,
      });
      setLunareSummary(r.data);
      if (r.data?.saved > 0) {
        toast.success(`${r.data.saved}/${r.data.total} PDF-uri salvate în Facturi Lunare.`);
      } else {
        toast.error(`0 salvate — vezi rezumatul.`);
      }
      setLunarePickerOpen(false);
    } catch (e) {
      toast.error("Salvare eșuată: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLunareBusy(false);
    }
  };

  return (
    <>
      <div className="border-b border-zinc-800 p-4">
        <div className="mb-2 flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 flex-1 basis-full items-start gap-2 sm:basis-auto">
            {onBack && (
              <button
                data-testid="msg-back-mobile"
                onClick={onBack}
                className="rounded-md p-1.5 text-zinc-400 hover:bg-zinc-900 hover:text-zinc-100 lg:hidden shrink-0"
                title="Înapoi la listă"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
            )}
            <h2 className="min-w-0 break-words text-base font-semibold leading-tight text-zinc-50">{msg.subject || "(fără subiect)"}</h2>
          </div>
          <div className="flex flex-wrap gap-1">
            <button data-testid="msg-star-btn" onClick={onStar} className="rounded-md p-1.5 text-zinc-400 hover:bg-zinc-900 hover:text-amber-300">
              {msg.is_starred ? <Star className="h-4 w-4 fill-amber-400 text-amber-400" /> : <StarOff className="h-4 w-4" />}
            </button>
            {folder === "trash" ? (
              <>
                <button data-testid="msg-restore-btn" onClick={onRestore} className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/15 px-3 py-1.5 text-xs font-medium text-emerald-100 hover:bg-emerald-500/25" title="Restaurare">
                  Restore
                </button>
                <button data-testid="msg-delete-forever-btn" onClick={onDeleteForever} className="rounded-md p-1.5 text-zinc-400 hover:bg-rose-500/20 hover:text-rose-300" title="Șterge definitiv">
                  <Trash className="h-4 w-4" />
                </button>
              </>
            ) : (
              <button data-testid="msg-delete-btn" onClick={onDelete} className="rounded-md p-1.5 text-zinc-400 hover:bg-rose-500/15 hover:text-rose-300" title="Mută în Coș">
                <Trash className="h-4 w-4" />
              </button>
            )}
            <button data-testid="msg-reply-btn" onClick={onReply} className="inline-flex items-center gap-1.5 rounded-md border border-indigo-500/50 bg-indigo-500/20 px-3 py-1.5 text-xs font-medium text-indigo-100 hover:bg-indigo-500/30">
              <Reply className="h-3.5 w-3.5" /> Răspunde
            </button>
            <button data-testid="msg-reply-all-btn" onClick={onReplyAll} className="inline-flex items-center gap-1.5 rounded-md border border-indigo-500/30 bg-indigo-500/10 px-3 py-1.5 text-xs font-medium text-indigo-200 hover:bg-indigo-500/20">
              <ReplyAll className="h-3.5 w-3.5" /> Răspunde tuturor
            </button>
            <button data-testid="msg-forward-btn" onClick={onForward} className="inline-flex items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-800 px-3 py-1.5 text-xs font-medium text-zinc-200 hover:border-zinc-600">
              <Forward className="h-3.5 w-3.5" /> Redirecționează
            </button>
            {hasPdfAttachments && (
              <button
                data-testid="msg-add-to-financiar-btn"
                onClick={importInvoicesFromMail}
                disabled={invoiceBusy}
                title="Scaneaza atasamentele PDF cu Gemini si le adauga in Financiar (aceleasi reguli ca Import bulk)"
                className="inline-flex items-center gap-1.5 rounded-md border border-cyan-500/50 bg-cyan-500/15 px-3 py-1.5 text-xs font-medium text-cyan-100 hover:bg-cyan-500/25 disabled:opacity-50"
              >
                {invoiceBusy ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Receipt className="h-3.5 w-3.5" />}
                {invoiceBusy ? "Se scaneaza..." : "Adauga la Financiar"}
              </button>
            )}
            {hasPdfAttachments && (
              <button
                data-testid="msg-add-to-facturi-lunare-btn"
                onClick={openLunarePicker}
                disabled={lunareBusy}
                title="Salveaza atasamentele selectate direct in Facturi > Lunare (fara a crea inregistrare Financiar)"
                className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/50 bg-emerald-500/15 px-3 py-1.5 text-xs font-medium text-emerald-100 hover:bg-emerald-500/25 disabled:opacity-50"
              >
                <FileText className="h-3.5 w-3.5" />
                {lunareBusy ? "Se salveaza..." : "Adauga la Facturi Lunare"}
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between text-[11px] text-zinc-400">
          <div>
            <p><span className="text-zinc-500">De la:</span> <span className="text-zinc-200">{msg.from_name || msg.from_addr}</span> <span className="text-zinc-600">&lt;{msg.from_addr}&gt;</span></p>
            <p><span className="text-zinc-500">Către:</span> {(msg.to_list || []).join(", ") || "—"}</p>
            {(msg.cc_list || []).length > 0 && <p><span className="text-zinc-500">CC:</span> {msg.cc_list.join(", ")}</p>}
          </div>
          <p className="text-zinc-500">{msg.date ? new Date(msg.date).toLocaleString("ro-RO") : ""}</p>
        </div>
        {(msg.attachments || []).length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            {msg.attachments.length > 1 && (
              <button
                data-testid="download-all-attachments"
                onClick={() => downloadAllAttachments(msg.id, msg.attachments)}
                className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-medium text-emerald-200 hover:bg-emerald-500/20"
                title="Descarcă toate atașamentele"
              >
                <Download className="h-3 w-3" /> Descarcă toate ({msg.attachments.length})
              </button>
            )}
            {msg.attachments.map((a, i) => (
              <span key={i} className="inline-flex items-center gap-0.5 rounded-md border border-zinc-800 bg-zinc-900 text-[11px] text-zinc-300 overflow-hidden">
                <button
                  data-testid={`att-${i}`}
                  onClick={() => openAttachment(msg.id, i, a.filename)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 hover:bg-zinc-800/60 hover:text-indigo-200"
                  title="Deschide"
                >
                  <Paperclip className="h-3 w-3" /> {a.filename}
                  <span className="text-zinc-600">({Math.round((a.size || 0) / 1024)} KB)</span>
                </button>
                <button
                  data-testid={`att-${i}-download`}
                  onClick={() => downloadAttachment(msg.id, i, a.filename)}
                  className="border-l border-zinc-800 px-2 py-1 text-zinc-500 hover:bg-zinc-800/60 hover:text-emerald-300"
                  title="Descarcă pe disk"
                >
                  <Download className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
      <div data-testid="msg-body" className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden bg-zinc-950 p-5">
        {msg.body_html ? (
          <div className="mail-html overflow-x-auto break-words rounded-lg bg-white p-5 text-sm text-gray-900 shadow-inner" dangerouslySetInnerHTML={{ __html: msg.body_html }} />
        ) : (
          <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-zinc-200">{msg.body_text || "(fără conținut)"}</pre>
        )}
      </div>
      <ImportSummaryModal open={!!invoiceSummary} data={invoiceSummary} onClose={() => setInvoiceSummary(null)} onRefresh={null} />

      {/* Popup: „Adauga la Facturi Lunare" — user selecteaza care PDF-uri
          si (optional) luna manuala; auto-detect via Gemini din invoice_date. */}
      {lunarePickerOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
             onClick={() => !lunareBusy && setLunarePickerOpen(false)}>
          <div className="w-full max-w-2xl rounded-2xl border border-zinc-800 bg-zinc-950 shadow-2xl"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-zinc-800 px-6 py-4">
              <div>
                <h3 className="text-lg font-semibold text-zinc-100">Adaugă la Facturi Lunare</h3>
                <p className="mt-0.5 text-[11px] text-zinc-500">
                  Selectează PDF-urile de salvat. Lună lasă gol = auto-detect din PDF (via Gemini).
                </p>
              </div>
              <button data-testid="lunare-picker-close" disabled={lunareBusy}
                onClick={() => setLunarePickerOpen(false)}
                className="rounded-md p-2 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200 disabled:opacity-40">✕</button>
            </div>
            <div className="max-h-[55vh] overflow-y-auto px-4 py-3 space-y-2">
              {(msg.attachments || []).map((a, idx) => {
                if (!(a.filename || "").toLowerCase().endsWith(".pdf")) return null;
                const row = lunareRows[idx] || { selected: false, month: "" };
                return (
                  <div key={idx} className="flex items-center gap-3 rounded border border-zinc-800 bg-zinc-900/40 px-3 py-2">
                    <input type="checkbox"
                      data-testid={`lunare-pick-${idx}`}
                      checked={row.selected}
                      onChange={(e) => setLunareRows(prev => ({...prev, [idx]: {...row, selected: e.target.checked}}))}
                      className="h-4 w-4 rounded border-zinc-700 bg-zinc-950" />
                    <div className="flex-1 min-w-0">
                      <div className="truncate text-sm font-medium text-zinc-100" title={a.filename}>
                        {a.filename}
                      </div>
                      <div className="text-[10px] text-zinc-500">
                        {a.size ? `${Math.round(a.size / 1024)} KB` : ""} · {a.mime || "PDF"}
                      </div>
                    </div>
                    <input
                      type="month"
                      value={row.month}
                      data-testid={`lunare-month-${idx}`}
                      placeholder="auto"
                      onChange={(e) => setLunareRows(prev => ({...prev, [idx]: {...row, month: e.target.value}}))}
                      className="w-40 rounded border border-zinc-800 bg-zinc-950 px-2 py-1 text-xs text-zinc-100"
                      title="Lasă gol pentru auto-detect din PDF (Gemini)"
                    />
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-between border-t border-zinc-800 px-6 py-3">
              <div className="text-[11px] text-zinc-500">
                {Object.values(lunareRows).filter(r => r.selected).length} selectate din {Object.keys(lunareRows).length} PDF-uri
              </div>
              <div className="flex gap-2">
                <button data-testid="lunare-cancel" disabled={lunareBusy}
                  onClick={() => setLunarePickerOpen(false)}
                  className="rounded-md border border-zinc-700 bg-zinc-800 px-3 py-1.5 text-xs font-medium text-zinc-200 hover:bg-zinc-700 disabled:opacity-40">
                  Anulează
                </button>
                <button data-testid="lunare-submit" disabled={lunareBusy || Object.values(lunareRows).filter(r => r.selected).length === 0}
                  onClick={submitLunareSave}
                  className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/50 bg-emerald-500/20 px-3 py-1.5 text-xs font-medium text-emerald-100 hover:bg-emerald-500/30 disabled:opacity-40">
                  {lunareBusy ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
                  {lunareBusy ? "Se salvează..." : "Salvează selectate"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Post-save summary — reused ImportSummaryModal-like simple list */}
      {lunareSummary && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setLunareSummary(null)}>
          <div className="w-full max-w-lg rounded-2xl border border-zinc-800 bg-zinc-950 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-zinc-800 px-6 py-4">
              <h3 className="text-lg font-semibold text-zinc-100">Facturi Lunare · rezultat</h3>
              <button data-testid="lunare-summary-close" onClick={() => setLunareSummary(null)}
                className="rounded-md p-2 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200">✕</button>
            </div>
            <div className="grid grid-cols-3 gap-3 border-b border-zinc-800 p-4 text-center">
              <div><div className="text-[10px] uppercase text-zinc-500">Salvate</div><div className="mt-1 text-xl font-semibold text-emerald-300">{lunareSummary.saved}</div></div>
              <div><div className="text-[10px] uppercase text-zinc-500">Duplicate</div><div className="mt-1 text-xl font-semibold text-amber-300">{lunareSummary.skipped_dup}</div></div>
              <div><div className="text-[10px] uppercase text-zinc-500">Eșuate</div><div className="mt-1 text-xl font-semibold text-rose-300">{lunareSummary.failed}</div></div>
            </div>
            <div className="max-h-[40vh] overflow-y-auto px-4 py-3">
              {(lunareSummary.results || []).map((r, i) => (
                <div key={i} className={`mb-1 rounded border px-2 py-1 text-[11px] ${
                  r.ok ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-200"
                       : "border-rose-500/30 bg-rose-500/5 text-rose-200"}`}>
                  {r.ok
                    ? <>✓ {r.filename} · luna {r.monthly_invoice?.month}</>
                    : <>✕ {r.filename} · {r.error}</>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function AccountModal({ initial, onClose, onSaved }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState({
    name: initial?.name || "",
    email: initial?.email || "",
    password: "",
    imap_host: initial?.imap_host || "",
    imap_port: initial?.imap_port || 993,
    imap_ssl: initial?.imap_ssl ?? true,
    smtp_host: initial?.smtp_host || "",
    smtp_port: initial?.smtp_port || 587,
    smtp_ssl: initial?.smtp_ssl ?? false,
    signature_html: initial?.signature_html || "",
    backfill_days: initial?.backfill_days || 7,
    is_active: initial?.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [advanced, setAdvanced] = useState(false);

  const handleSave = async () => {
    if (!form.email) { toast.error("Email obligatoriu"); return; }
    if (!isEdit && !form.password) { toast.error("Parolă obligatorie"); return; }
    if (!form.name) { toast.error("Nume obligatoriu"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        const upd = { ...form };
        if (!upd.password) delete upd.password;
        await axios.put(`${API}/mail/accounts/${initial.id}`, upd);
        toast.success("Cont actualizat");
      } else {
        await axios.post(`${API}/mail/accounts`, form);
        toast.success("Cont adăugat și conectat");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm("Sigur ștergi acest cont și toate mesajele lui?")) return;
    try {
      await axios.delete(`${API}/mail/accounts/${initial.id}`);
      toast.success("Cont șters");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="account-modal">
      <div className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-xl border border-zinc-800 bg-zinc-950 shadow-2xl">
        <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-3">
          <h2 className="text-sm font-semibold text-zinc-100">{isEdit ? "Editează cont email" : "Adaugă cont email"}</h2>
          <button onClick={onClose} className="rounded-md p-1 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex-1 space-y-3 overflow-y-auto p-4">
          <Field label="Nume afișat">
            <input data-testid="acc-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Ex: Gmail Bragus" className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 focus:border-indigo-500/50 focus:outline-none" />
          </Field>
          <Field label="Email">
            <input data-testid="acc-email" type="email" disabled={isEdit} value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="exemplu@gmail.com" className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 focus:border-indigo-500/50 focus:outline-none disabled:opacity-50" />
            <p className="mt-1 text-[10px] text-zinc-500">Pentru Gmail / Yahoo / Outlook / bragus.ro, IMAP/SMTP sunt detectate automat.</p>
          </Field>
          <Field label={isEdit ? "Parolă nouă (lasă gol = nu se schimbă)" : "Parolă (sau App Password)"}>
            <input data-testid="acc-password" type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder={isEdit ? "•••••••• (neschimbat)" : "••••••••••••••••"} className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 focus:border-indigo-500/50 focus:outline-none" />
            {form.email?.endsWith("@gmail.com") && (
              <p className="mt-1 text-[10px] text-amber-300/80">Pentru Gmail folosește un <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="underline">App Password</a> (2FA necesar).</p>
            )}
          </Field>
          <Field label="Semnătură (HTML acceptat)">
            <textarea data-testid="acc-signature" value={form.signature_html} onChange={e => setForm({...form, signature_html: e.target.value})} rows={4} placeholder="<p>Cu bine,<br/>Numele tău</p>" className="w-full resize-y rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-xs font-mono text-zinc-100 focus:border-indigo-500/50 focus:outline-none" />
            {isEdit && <SignatureImageUpload accountId={initial.id} currentSig={form.signature_html} onUploaded={(html) => setForm(f => ({ ...f, signature_html: html }))} />}
            {!isEdit && <p className="mt-1 text-[10px] text-zinc-500">După salvarea contului vei putea încărca o imagine pentru semnătură.</p>}
            {form.signature_html && (
              <div className="mt-2 rounded-md border border-zinc-800 bg-white p-2 text-xs text-gray-900" dangerouslySetInnerHTML={{ __html: form.signature_html }} />
            )}
          </Field>
          <Field label="Backfill (zile)">
            <select data-testid="acc-backfill" value={form.backfill_days} onChange={e => setForm({...form, backfill_days: parseInt(e.target.value)})} className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 focus:border-indigo-500/50 focus:outline-none">
              <option value="3" className="bg-zinc-900">3 zile</option>
              <option value="7" className="bg-zinc-900">7 zile</option>
              <option value="14" className="bg-zinc-900">14 zile</option>
              <option value="30" className="bg-zinc-900">30 zile</option>
              <option value="90" className="bg-zinc-900">90 zile</option>
            </select>
          </Field>

          <button onClick={() => setAdvanced(v => !v)} className="text-[11px] text-indigo-300 hover:text-indigo-200">{advanced ? "Ascunde" : "Setări avansate (IMAP/SMTP manual)"}</button>
          {advanced && (
            <div className="space-y-3 rounded-md border border-zinc-800 bg-zinc-900/40 p-3">
              <div className="grid grid-cols-2 gap-2">
                <Field label="IMAP host"><input value={form.imap_host} onChange={e => setForm({...form, imap_host: e.target.value})} className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100" /></Field>
                <Field label="IMAP port"><input type="number" value={form.imap_port} onChange={e => setForm({...form, imap_port: parseInt(e.target.value)})} className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100" /></Field>
              </div>
              <label className="flex items-center gap-2 text-xs text-zinc-300"><input type="checkbox" checked={form.imap_ssl} onChange={e => setForm({...form, imap_ssl: e.target.checked})} className="accent-indigo-500" /> IMAP SSL</label>
              <div className="grid grid-cols-2 gap-2">
                <Field label="SMTP host"><input value={form.smtp_host} onChange={e => setForm({...form, smtp_host: e.target.value})} className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100" /></Field>
                <Field label="SMTP port"><input type="number" value={form.smtp_port} onChange={e => setForm({...form, smtp_port: parseInt(e.target.value)})} className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100" /></Field>
              </div>
              <label className="flex items-center gap-2 text-xs text-zinc-300"><input type="checkbox" checked={form.smtp_ssl} onChange={e => setForm({...form, smtp_ssl: e.target.checked})} className="accent-indigo-500" /> SMTP SSL (port 465). Lasă debifat pentru STARTTLS (587).</label>
            </div>
          )}
        </div>
        <div className="flex items-center justify-between border-t border-zinc-800 px-4 py-3">
          {isEdit ? (
            <button data-testid="acc-delete-btn" onClick={handleDelete} className="inline-flex items-center gap-1 rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-1.5 text-xs text-rose-200 hover:bg-rose-500/20">
              <Trash2 className="h-3 w-3" /> Șterge cont
            </button>
          ) : <div />}
          <div className="flex gap-2">
            <button onClick={onClose} className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300 hover:bg-zinc-800">Anulează</button>
            <button data-testid="acc-save-btn" onClick={handleSave} disabled={saving} className="inline-flex items-center gap-1.5 rounded-md border border-indigo-500/50 bg-indigo-500/20 px-3 py-1.5 text-xs font-medium text-indigo-100 hover:bg-indigo-500/30 disabled:opacity-50">
              <CheckCircle2 className="h-3.5 w-3.5" /> {saving ? "Se conectează…" : "Salvează & Conectează"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ComposeModal({ initial, accounts, onClose, onSent }) {
  const [form, setForm] = useState({
    account_id: initial.account_id,
    to: (initial.to || []).join(", "),
    cc: (initial.cc || []).join(", "),
    subject: initial.subject || "",
    body: initial.body || "",
    in_reply_to: initial.in_reply_to || "",
    references: initial.references || "",
    signature: initial.signature || "",
    forward_from_mid: initial.forward_from_mid || "",
    include_forward_attachments: !!initial.include_forward_attachments,
  });
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [showSignature, setShowSignature] = useState(false);
  const fileRef = useRef(null);

  // When the user switches "From" account, prefill the signature with the new
  // account's default (only when not yet customized for this email).
  const handleAccountChange = (accId) => {
    const acc = accounts.find(a => a.id === accId);
    const accSig = acc?.signature_html || "";
    setForm(f => ({ ...f, account_id: accId, signature: f.signature && f.signature !== (accounts.find(a => a.id === f.account_id)?.signature_html || "") ? f.signature : accSig }));
  };

  const handleFiles = (list) => {
    if (!list) return;
    const arr = Array.from(list);
    setFiles((prev) => [...prev, ...arr]);
  };

  const handleSend = async () => {
    if (!form.to.trim()) { toast.error("Destinatar obligatoriu"); return; }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("to", form.to);
      fd.append("cc", form.cc);
      fd.append("subject", form.subject);
      fd.append("body_text", form.body);
      if (form.in_reply_to) fd.append("in_reply_to", form.in_reply_to);
      if (form.references) fd.append("references", form.references);
      if (form.signature) fd.append("signature_override", form.signature);
      if (form.forward_from_mid) {
        fd.append("forward_from_mid", form.forward_from_mid);
        fd.append("include_forward_attachments", form.include_forward_attachments ? "true" : "false");
      }
      for (const f of files) fd.append("files", f);
      await axios.post(`${API}/mail/accounts/${form.account_id}/send`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Email trimis");
      onSent();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-end bg-black/80 p-0 sm:items-center sm:justify-center sm:p-4" data-testid="compose-modal">
      <div className="flex h-full max-h-screen w-full max-w-2xl flex-col rounded-none border border-zinc-800 bg-zinc-950 shadow-2xl sm:h-auto sm:max-h-[90vh] sm:rounded-xl">
        <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-3">
          <h2 className="text-sm font-semibold text-zinc-100">{
            form.forward_from_mid ? "Redirecționează" :
            initial.in_reply_to ? "Răspunde" :
            "Mail nou"
          }{form.forward_from_mid && form.include_forward_attachments ? " (cu atașamente)" : ""}</h2>
          <button onClick={onClose} className="rounded-md p-1 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex-1 space-y-2 overflow-y-auto p-4">
          <Field label="De la">
            <select data-testid="compose-from" value={form.account_id} onChange={e => handleAccountChange(e.target.value)} className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100">
              {accounts.map(a => <option key={a.id} value={a.id} className="bg-zinc-900">{a.name} &lt;{a.email}&gt;</option>)}
            </select>
          </Field>
          <Field label="Către (separate prin virgulă)">
            <input data-testid="compose-to" value={form.to} onChange={e => setForm({...form, to: e.target.value})} placeholder="destinatar@example.com" className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100" />
          </Field>
          <Field label="CC (opțional)">
            <input data-testid="compose-cc" value={form.cc} onChange={e => setForm({...form, cc: e.target.value})} placeholder="cc@example.com" className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100" />
          </Field>
          <Field label="Subiect">
            <input data-testid="compose-subject" value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100" />
          </Field>
          <Field label="Mesaj (semnătura se atașează automat)">
            <textarea data-testid="compose-body" value={form.body} onChange={e => setForm({...form, body: e.target.value})} rows={12} className="w-full resize-y rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100" />
          </Field>
          <div>
            <button
              data-testid="compose-toggle-signature"
              type="button"
              onClick={() => setShowSignature(v => !v)}
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-indigo-300 hover:text-indigo-200"
            >
              {showSignature ? "Ascunde" : "Editează"} semnătura pentru acest email
            </button>
            {showSignature && (
              <textarea
                data-testid="compose-signature"
                value={form.signature}
                onChange={e => setForm({...form, signature: e.target.value})}
                rows={5}
                placeholder="<p>Cu bine,<br/>Numele tău</p>"
                className="mt-1 w-full resize-y rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-xs font-mono text-zinc-100 focus:border-indigo-500/50 focus:outline-none"
              />
            )}
            {!showSignature && form.signature && (
              <p className="mt-1 text-[10px] text-zinc-500">Folosesc semnătura standard a contului. <span className="text-indigo-300 cursor-pointer hover:underline" onClick={() => setShowSignature(true)}>Modifică doar pentru acest email →</span></p>
            )}
          </div>
          {files.length > 0 && (
            <div className="space-y-1">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500">Atașamente</p>
              {files.map((f, i) => (
                <div key={i} className="flex items-center justify-between gap-2 rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-xs">
                  <span className="truncate text-zinc-300"><Paperclip className="inline h-3 w-3 mr-1" />{f.name} <span className="text-zinc-600">({Math.round(f.size/1024)} KB)</span></span>
                  <button data-testid={`compose-att-remove-${i}`} onClick={() => setFiles(files.filter((_,j) => j !== i))} className="rounded p-1 text-zinc-500 hover:text-rose-300"><X className="h-3 w-3" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center justify-between border-t border-zinc-800 px-4 py-3">
          <input ref={fileRef} type="file" multiple hidden onChange={e => handleFiles(e.target.files)} />
          <button data-testid="compose-attach-btn" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300 hover:border-indigo-500/40 hover:text-indigo-200">
            <Paperclip className="h-3.5 w-3.5" /> Atașează fișier
          </button>
          <button data-testid="compose-send-btn" onClick={handleSend} disabled={sending} className="inline-flex items-center gap-1.5 rounded-md border border-indigo-500/50 bg-indigo-500/20 px-4 py-1.5 text-xs font-medium text-indigo-100 hover:bg-indigo-500/30 disabled:opacity-50">
            <Send className="h-3.5 w-3.5" /> {sending ? "Se trimite…" : "Trimite"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-zinc-500">{label}</label>
      {children}
    </div>
  );
}

function SignatureImageUpload({ accountId, currentSig, onUploaded }) {
  const [uploading, setUploading] = useState(false);
  const [maxWidth, setMaxWidth] = useState(500);
  const [replace, setReplace] = useState(true);
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Doar imagini sunt acceptate (PNG/JPG/GIF)");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Imaginea trebuie să fie sub 2MB. Comprimă-o întâi.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("replace", replace ? "true" : "false");
      fd.append("max_width_px", String(maxWidth));
      const r = await axios.post(
        `${API}/mail/accounts/${accountId}/upload-signature-image`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (r.data?.ok) {
        // Refresh signature_html from server
        const acc = await axios.get(`${API}/mail/accounts`);
        const updated = (acc.data?.items || []).find(a => a.id === accountId);
        if (updated) onUploaded(updated.signature_html || "");
        toast.success(`Imagine semnătură încărcată (${Math.round((r.data.size_bytes || 0)/1024)} KB)`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="mt-2 rounded-md border border-zinc-800 bg-zinc-900/40 p-2 space-y-2">
      <div className="flex items-center gap-2 text-[10px] text-zinc-400">
        <label className="flex items-center gap-1 cursor-pointer">
          <input type="radio" checked={replace} onChange={() => setReplace(true)} className="accent-indigo-500" />
          Înlocuiește semnătura existentă
        </label>
        <label className="flex items-center gap-1 cursor-pointer">
          <input type="radio" checked={!replace} onChange={() => setReplace(false)} className="accent-indigo-500" />
          Adaugă sub textul existent
        </label>
        <div className="grow" />
        <label className="flex items-center gap-1">
          Lățime max:
          <select value={maxWidth} onChange={(e) => setMaxWidth(parseInt(e.target.value))}
            className="rounded border border-zinc-700 bg-zinc-900 px-1 py-0.5">
            <option value={300}>300px</option>
            <option value={400}>400px</option>
            <option value={500}>500px</option>
            <option value={600}>600px</option>
            <option value={800}>800px</option>
          </select>
        </label>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        data-testid="acc-signature-image-upload"
        onChange={(e) => handleFile(e.target.files?.[0])}
        disabled={uploading}
        className="block w-full text-[11px] text-zinc-300 file:mr-2 file:rounded-md file:border file:border-indigo-500/40 file:bg-indigo-500/10 file:px-3 file:py-1.5 file:text-[11px] file:font-medium file:text-indigo-200 hover:file:bg-indigo-500/20 disabled:opacity-50"
      />
      {uploading && (
        <div className="text-[10px] text-indigo-300 flex items-center gap-1.5">
          <ImageIcon className="h-3 w-3 animate-pulse" /> Se încarcă imaginea…
        </div>
      )}
      <p className="text-[10px] text-zinc-500">
        Imaginea e încorporată ca base64 — va apărea în TOATE emailurile noi și răspunsurile. Recomandat: PNG/JPG sub 200 KB.
      </p>
    </div>
  );
}

